import { Button, Input, Spinner } from '@bennows/boows-react';
import type { components } from '@social-studio/operator-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiFailure, call } from '../../lib/api';
import { safeReturnTo } from '../../lib/session';
import { passkeysSupported } from '../../lib/webauthn';
import { AuthCard } from './AuthCard';
import { CodeStep } from './CodeStep';
import { PasskeyStep } from './PasskeyStep';
import { TotpSetupStep } from './TotpSetupStep';
import { ME_QUERY_KEY, useOperatorMe } from './useOperatorMe';

type SecondFactor = components['schemas']['OperatorSignInResultDto']['second_factor'];

function passwordFailureKey(error: unknown): string | null {
  if (!error) return null;
  const slug = error instanceof ApiFailure ? error.slug : 'unexpected';
  if (slug === 'invalid-credentials') return 'signIn.invalidCredentials';
  if (slug === 'validation-failed') return 'signIn.invalidEmail';
  if (slug === 'rate-limited') return 'common.rateLimited';
  if (slug === 'network') return 'common.errorNetwork';
  return 'common.errorUnexpected';
}

/**
 * Operator sign-in (spec 140 REQ-140-004) in two steps on one page: password,
 * then a passkey when the operator has one (T460-1-4), the authenticator code
 * otherwise or on request - or, without an authenticator yet, its setup.
 * A pending session (reload after step 1) continues at step 2.
 */
export function SignInPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('return_to'));
  const me = useOperatorMe();
  const [step, setStep] = useState<SecondFactor | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [missing, setMissing] = useState({ email: false, password: false });
  const headingId = useId();
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const messageRef = useRef<HTMLParagraphElement>(null);

  const submit = useMutation({
    mutationFn: () => call(() => api.POST('/v1/operator/sessions', { body: { email: email.trim(), password } })),
    onSuccess: async (result) => {
      setPassword('');
      // Nothing cached for a previous session may show up in this one.
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== ME_QUERY_KEY[0] });
      await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
      setStep(result.second_factor);
    },
    onError: (error) => {
      if (error instanceof ApiFailure && error.slug === 'invalid-credentials') setPassword('');
    },
  });
  const failure = submit.isPending ? null : passwordFailureKey(submit.error);

  useEffect(() => {
    if (!failure) return;
    if (failure === 'signIn.invalidEmail') emailRef.current?.focus();
    else messageRef.current?.focus();
  }, [failure]);

  const pending = me.data && !me.data.verified ? (!me.data.totp_confirmed ? 'totp_setup' : me.data.passkeys > 0 ? 'passkey' : 'totp') : null;
  const chosen = step ?? pending;
  // Without WebAuthn in this browser the passkey step could only fail.
  const passkeyUsable = passkeysSupported();
  const current = chosen === 'passkey' && !passkeyUsable ? 'totp' : chosen;
  const hasPasskey = passkeyUsable && (me.data?.passkeys ?? 0) > 0;

  if (me.data?.verified) return <Navigate to={returnTo} replace />;
  if (me.isPending && !step) {
    return (
      <AuthCard>
        <Spinner label={t('common.loading')} />
      </AuthCard>
    );
  }

  const startOver = async () => {
    // Ends the pending session, so the next sign-in starts clean.
    await api.DELETE('/v1/operator/sessions/current').catch(() => undefined);
    queryClient.setQueryData(ME_QUERY_KEY, null);
    submit.reset();
    setStep(null);
  };
  const onVerified = () => navigate(returnTo, { replace: true });

  if (current === 'passkey') {
    return (
      <AuthCard>
        <PasskeyStep onVerified={onVerified} onUseCode={() => setStep('totp')} onStartOver={startOver} />
      </AuthCard>
    );
  }
  if (current === 'totp') {
    return (
      <AuthCard>
        <CodeStep onVerified={onVerified} onStartOver={startOver} onUsePasskey={hasPasskey ? () => setStep('passkey') : undefined} />
      </AuthCard>
    );
  }
  if (current === 'totp_setup') {
    return (
      <AuthCard>
        <TotpSetupStep onVerified={onVerified} onStartOver={startOver} />
      </AuthCard>
    );
  }

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const empty = { email: email.trim() === '', password: password === '' };
    setMissing(empty);
    if (empty.email) return emailRef.current?.focus();
    if (empty.password) return passwordRef.current?.focus();
    submit.mutate();
  };
  const emailHelp = missing.email ? t('signIn.required') : failure === 'signIn.invalidEmail' ? t(failure) : undefined;
  const formMessage = failure && failure !== 'signIn.invalidEmail' ? failure : null;

  return (
    <AuthCard>
      <h1 id={headingId}>{t('signIn.title')}</h1>
      <p className="ui-auth__lead">{t('signIn.lead')}</p>
      <form onSubmit={onSubmit} noValidate aria-labelledby={headingId}>
        <Input
          ref={emailRef}
          label={t('signIn.email')}
          type="email"
          autoComplete="username"
          value={email}
          onChange={setEmail}
          required
          invalid={emailHelp !== undefined}
          help={emailHelp}
        />
        <Input
          ref={passwordRef}
          label={t('signIn.password')}
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={setPassword}
          required
          invalid={missing.password}
          help={missing.password ? t('signIn.required') : undefined}
        />
        {formMessage ? (
          <p className="ui-auth__message" role="alert" tabIndex={-1} ref={messageRef}>
            {t(formMessage)}
          </p>
        ) : null}
        <Button type="submit" loading={submit.isPending} block>
          {t('signIn.submit')}
        </Button>
      </form>
    </AuthCard>
  );
}
